import {
  InvoiceSummary,
  InvoiceSummaryRawData,
  useInvoiceDiscount,
  useInvoiceSummary,
  UseInvoiceSummaryProps,
  useServiceChargeCatalogs,
  useTaxRate,
} from '@melio/ar-domain';
import { isUndefined, uniq } from 'lodash';
import { useMemo } from 'react';

type ErrorFn = (error: Error) => void;

export type UseInvoiceSummaryEnrichedProps = UseInvoiceSummaryProps;

export type UseInvoiceSummaryModel = {
  isLoading: boolean;
  isFetching: boolean;
  data?: InvoiceSummaryRawData;
};

export const useInvoiceSummaryEnriched = ({
  params,
  onError,
  enabled,
  ...props
}: UseInvoiceSummaryEnrichedProps = {}) => {
  const { serviceChargesIds = [], discountId, taxRateId } = params ?? {};

  const { data: invoiceSummary, ...invoiceSummaryModel } = useInvoiceSummary({ params, onError, enabled, ...props });
  const taxRateQuery = useEnrichTaxRate({ invoiceSummary, id: taxRateId, onError, enabled });
  const discountQuery = useEnrichDiscount({ invoiceSummary, id: discountId, onError, enabled });
  const serviceChargesQuery = useEnrichServiceCharges({ serviceChargesIds, invoiceSummary, onError, enabled });

  const isLoading = [invoiceSummaryModel, taxRateQuery, discountQuery, serviceChargesQuery].some(
    (query) => query.isLoading
  );

  const isFetching = [invoiceSummaryModel, taxRateQuery, discountQuery, serviceChargesQuery].some(
    (query) => query.isFetching
  );

  return {
    isLoading,
    isFetching: isLoading || isFetching,
    data: invoiceSummary && {
      ...invoiceSummary,
      taxRate: taxRateQuery.data,
      discount: discountQuery.data,
      serviceCharges: serviceChargesQuery.data,
    },
  } as UseInvoiceSummaryModel;
};

type UseEnrichTaxRateProps = { invoiceSummary?: InvoiceSummary; id?: string; onError?: ErrorFn; enabled?: boolean };
export const useEnrichTaxRate = ({
  invoiceSummary: { taxRate } = createEmptyInvoiceSummary(),
  ...props
}: UseEnrichTaxRateProps) => {
  const { data: _data = null, ...query } = useTaxRate(props);
  const data = useMemo(() => taxRate && _data && { ...taxRate, name: _data.name, rate: _data.rate }, [taxRate, _data]);
  return { ...query, data };
};

type UseEnrichDiscountProps = {
  invoiceSummary?: InvoiceSummary;
  id?: string;
  onError?: ErrorFn;
  enabled?: boolean;
};
export const useEnrichDiscount = ({
  invoiceSummary: { discount } = createEmptyInvoiceSummary(),
  id,
  ...props
}: UseEnrichDiscountProps) => {
  const { data: _data = null, ...query } = useInvoiceDiscount({ id, ...props });

  const data = useMemo(
    () => discount && _data && { ...discount, name: _data.name, type: _data.type, value: _data.value },
    [discount, _data]
  );

  return { ...query, data };
};

type UseEnrichServiceChargesProps = {
  invoiceSummary?: InvoiceSummary;
  serviceChargesIds?: string[];
  onError?: ErrorFn;
  enabled?: boolean;
};
export const useEnrichServiceCharges = ({
  invoiceSummary: { serviceCharges } = createEmptyInvoiceSummary(),
  serviceChargesIds = [],
  enabled = true,
  onError,
}: UseEnrichServiceChargesProps) => {
  const { data: _data, ...query } = useServiceChargeCatalogs({
    params: { ids: uniq(serviceChargesIds) },
    enabled: enabled && serviceChargesIds.length > 0,
    onError,
  });

  const data = useMemo(() => {
    if (!serviceChargesIds.length || !serviceCharges.length || !_data) {
      return [];
    }
    return serviceChargesIds
      .map((id) => {
        const calculateValue = serviceCharges.find((serviceCharge) => serviceCharge.id === id)?.calculateValue;
        const serviceCharge = _data.find(({ id: serviceChargeId }) => serviceChargeId === id);

        if (isUndefined(serviceCharge) || isUndefined(calculateValue)) {
          return null;
        }
        return {
          id,
          calculateValue,
          name: serviceCharge.name,
          type: serviceCharge.type,
          value: serviceCharge.value,
        };
      })
      .filter(Boolean) as InvoiceSummaryRawData['serviceCharges'];
  }, [serviceCharges, _data, serviceChargesIds]);

  return { ...query, data };
};

const createEmptyInvoiceSummary = (): InvoiceSummary => ({
  discount: null,
  serviceCharges: [],
  subTotal: 0,
  taxRate: null,
  totalAmount: 0,
});
